import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Loader, X } from 'lucide-react';
import PaymentAPI from '../services/paymentApi';
import AlertModal from '../components/common/AlertModal';

const PageWrapper = styled.div`
  padding: 12px 24px 40px 24px;
  max-width: 1200px;
  margin: 0 auto;
  color: #1f2937;
  min-height: calc(100vh - 80px);
  
  @media (min-width: 1400px) {
    max-width: 1300px;
    padding: 16px 32px 50px 32px;
  }
  
  @media (min-width: 1600px) {
    max-width: 1400px;
    padding: 20px 48px 60px 48px;
  }
`;

const Title = styled.h2`
  font-size: 32px;
  font-weight: 700;
  text-align: left;
  margin-bottom: 20px;
  color: #1f2937;
  
  @media (min-width: 1400px) {
    font-size: 34px;
    margin-bottom: 24px;
  }
  
  @media (min-width: 1600px) {
    font-size: 36px;
    margin-bottom: 28px;
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 28px;
  background-color: #ffffff;
  padding: 28px;
  border-radius: 16px;
  min-height: 440px;
  align-items: start;
  border: 1px solid transparent;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;

  /* 旋转渐变边框动效 */
  &::before {
    content: '';
    position: absolute;
    width: 200px;
    background-image: linear-gradient(180deg, rgb(0, 183, 255), rgb(255, 48, 255));
    height: 320%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(0deg);
    animation: rotBGimg 5s linear infinite;
    transition: all 0.2s linear;
    z-index: 0;
  }

  /* 内层遮罩，露出边框 */
  &::after {
    content: '';
    position: absolute;
    background: #ffffff;
    inset: 6px;
    border-radius: 12px;
    z-index: 1;
  }

  /* 确保内容在遮罩之上 */
  > * {
    position: relative;
    z-index: 2;
  }

  @keyframes rotBGimg {
    from {
      transform: translate(-50%, -50%) rotate(0deg);
    }
    to {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
  
  @media (min-width: 1400px) {
    gap: 32px;
    padding: 32px;
    border-radius: 18px;
    min-height: 480px;
    grid-template-columns: 1.15fr 0.85fr;
  }
  
  @media (min-width: 1600px) {
    gap: 36px;
    padding: 36px;
    border-radius: 20px;
    min-height: 520px;
    grid-template-columns: 1.2fr 0.8fr;
  }
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const BenefitsSection = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  
  h3 {
    font-size: 21px;
    font-weight: 700;
    margin-bottom: 5px;
    line-height: 1.3;
    color: #1f2937;
  }
  p {
    color: #374151;
    margin-bottom: 14px;
    line-height: 1.5;
    font-weight: 500;
  }
  h4 {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 11px;
    color: #1f2937;
  }
  
  @media (min-width: 1400px) {
    h3 {
      font-size: 23px;
      margin-bottom: 6px;
    }
    p {
      font-size: 15px;
      margin-bottom: 16px;
    }
    h4 {
      font-size: 17px;
      margin-bottom: 12px;
    }
  }
  
  @media (min-width: 1600px) {
    h3 {
      font-size: 25px;
      margin-bottom: 8px;
    }
    p {
      font-size: 16px;
      margin-bottom: 18px;
    }
    h4 {
      font-size: 18px;
      margin-bottom: 14px;
    }
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 18px 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    font-size: 14px;
    color: #374151;
    margin-bottom: 11px;
    line-height: 1.4;
    font-weight: 500;
  }
  
  @media (min-width: 1400px) {
    margin: 0 0 20px 0;
    
    li {
      gap: 10px;
      font-size: 14.5px;
      margin-bottom: 12px;
      line-height: 1.45;
    }
  }
  
  @media (min-width: 1600px) {
    margin: 0 0 24px 0;
    
    li {
      gap: 12px;
      font-size: 15px;
      margin-bottom: 14px;
      line-height: 1.5;
    }
  }
`;

const SocialProof = styled.div`
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 11px;

  img {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 2px solid #ffffff;
    margin-left: -9px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    &:first-child {
      margin-left: 0;
    }
  }
  
  .stars {
    color: #f59e0b;
  }

  .text {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
  }
  
  @media (min-width: 1400px) {
    margin-top: 20px;
    padding-top: 20px;
    gap: 12px;
    
    img {
      width: 36px;
      height: 36px;
      margin-left: -9px;
    }
    
    .text {
      font-size: 14.5px;
    }
  }
  
  @media (min-width: 1600px) {
    margin-top: 24px;
    padding-top: 24px;
    gap: 14px;
    
    img {
      width: 38px;
      height: 38px;
      margin-left: -10px;
    }
    
    .text {
      font-size: 15px;
    }
  }
`;

const PricingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 100%;
  justify-content: ${props => props.isVip ? 'center' : 'flex-start'};
  align-items: stretch;
  
  @media (min-width: 1400px) {
    gap: 20px;
  }
  
  @media (min-width: 1600px) {
    gap: 24px;
  }
`;

const PlanCard = styled.div`
  background: #f9fafb;
  padding: 28px 28px 20px 28px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;

  h3 {
    font-size: 19px;
    font-weight: 700;
    margin-bottom: 12px;
    line-height: 1.3;
    color: #1f2937;
  }
  p {
    color: #374151;
    margin-bottom: 0;
    line-height: 1.4;
    font-weight: 500;
  }
  
  @media (min-width: 1400px) {
    padding: 30px 30px 24px 30px;
    border-radius: 18px;
    
    h3 {
      font-size: 21px;
      margin-bottom: 14px;
    }
    p {
      font-size: 15px;
    }
  }
  
  @media (min-width: 1600px) {
    padding: 36px 36px 28px 36px;
    border-radius: 20px;
    
    h3 {
      font-size: 22px;
      margin-bottom: 16px;
    }
    p {
      font-size: 16px;
    }
  }
`;

const MemberBadge = styled.div`
  text-align: center;
  margin-bottom: 16px;
  padding: 12px 20px;
  background: transparent;
  border-radius: 12px;
  color: #1f2937;
  font-size: 15px;
  font-weight: 500;
  
  .member-level {
    font-size: 17px;
    font-weight: 700;
    margin-top: 4px;
    color: #667eea;
  }
  
  @media (min-width: 1400px) {
    margin-bottom: 18px;
    padding: 14px 22px;
    border-radius: 13px;
    font-size: 15.5px;
    
    .member-level {
      font-size: 19px;
      margin-top: 5px;
    }
  }
  
  @media (min-width: 1600px) {
    margin-bottom: 20px;
    padding: 16px 24px;
    border-radius: 14px;
    font-size: 16px;
    
    .member-level {
      font-size: 20px;
      margin-top: 6px;
    }
  }
`;

const CustomTopUpCard = styled.div`
  background: #f9fafb;
  padding: 22px 22px 18px 22px;
  border-radius: 16px;
  text-align: left;
  border: 1px solid #e5e7eb;

  h3 {
    font-size: 19px;
    font-weight: 700;
    margin-bottom: 14px;
    color: #1f2937;
    line-height: 1.3;
  }
  
  @media (min-width: 1400px) {
    padding: 28px 28px 20px 28px;
    border-radius: 18px;
    
    h3 {
      font-size: 21px;
      margin-bottom: 16px;
    }
  }
  
  @media (min-width: 1600px) {
    padding: 32px 32px 24px 32px;
    border-radius: 20px;
    
    h3 {
      font-size: 22px;
      margin-bottom: 18px;
    }
  }
`;

const PlansGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 24px;
  margin-bottom: 8px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid ${props => (props.popular ? '#4f46e5' : '#e5e7eb')};
  border-radius: 12px;
  padding: 24px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 100px;
  box-shadow: ${props => (props.popular ? '0 4px 6px -1px rgba(79, 70, 229, 0.1), 0 2px 4px -1px rgba(79, 70, 229, 0.06)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)')};

  &:hover {
    border-color: #4f46e5;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px -5px rgba(79, 70, 229, 0.25), 0 10px 10px -5px rgba(79, 70, 229, 0.04);
  }
  
  @media (min-width: 1400px) {
    padding: 26px 22px;
    border-radius: 13px;
    min-height: 105px;
  }
  
  @media (min-width: 1600px) {
    padding: 28px 24px;
    border-radius: 14px;
    min-height: 110px;
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -11px;
  left: 22px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  padding: 4px 11px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(79, 70, 229, 0.3);
`;

const PlanInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PlanInfo = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.4;
  
  @media (min-width: 1400px) {
    font-size: 18px;
  }
  
  @media (min-width: 1600px) {
    font-size: 19px;
  }
`;

const PlanCredits = styled.div`
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  line-height: 1.5;
`;

const Price = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.3;
  gap: 4px;
  
  .promo-tag {
    font-size: 11px;
    color: #f97316;
    background: rgba(249, 115, 22, 0.08);
    padding: 2px 8px;
    border-radius: 9999px;
    font-weight: 600;
  }
  
  .current-row {
    display: flex;
    align-items: flex-end;
    font-size: 24px;
    font-weight: 800;
    color: #1f2937;
  }
  
  .current-row .period {
    font-size: 13px;
    color: #6b7280;
    font-weight: 500;
    margin-left: 2px;
  }
  
  @media (min-width: 1400px) {
    .current-row {
      font-size: 26px;
    }
    
    .current-row .period {
      font-size: 13.5px;
    }
  }
  
  @media (min-width: 1600px) {
    .current-row {
      font-size: 28px;
    }
    
    .current-row .period {
      font-size: 14px;
    }
  }
`;





// Custom top-up section styles moved to CustomTopUpCard above

const TopUpContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  
  @media (min-width: 1400px) {
    gap: 10px;
  }
  
  @media (min-width: 1600px) {
    gap: 12px;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 9px 11px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: #ffffff;
  color: #1f2937;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
  
  @media (min-width: 1400px) {
    padding: 10px 12px;
    border-radius: 9px;
    font-size: 14.5px;
  }
  
  @media (min-width: 1600px) {
    padding: 12px 14px;
    border-radius: 10px;
    font-size: 15px;
  }
`;

const TopUpButton = styled.button`
  background: ${props => props.disabled ? '#9ca3af' : '#4f46e5'};
  color: #ffffff;
  padding: 9px 22px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  height: 36px;
  white-space: nowrap;
  font-weight: 600;
  position: relative;

  &:hover {
    background: ${props => props.disabled ? '#9ca3af' : '#4338ca'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(79, 70, 229, 0.4)'};
  }

  &:active {
    transform: translateY(0);
  }
  
  @media (min-width: 1400px) {
    padding: 10px 24px;
    border-radius: 9px;
    font-size: 14.5px;
    height: 40px;
  }
  
  @media (min-width: 1600px) {
    padding: 12px 28px;
    border-radius: 10px;
    font-size: 15px;
    height: 42px;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #1f2937;
  color: #ffffff;
  font-size: 12px;
  border-radius: 6px;
  white-space: nowrap;
  opacity: ${props => props.show ? 1 : 0};
  pointer-events: ${props => props.show ? 'auto' : 'none'};
  transition: opacity 0.2s ease;
  z-index: 1000;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    right: 16px;
    border: 6px solid transparent;
    border-top-color: #1f2937;
  }
`;

const SubscriptionConfirmModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ConfirmModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: color 0.2s ease;
  border-radius: 4px;

  &:hover {
    color: #1f2937;
    background: #f3f4f6;
  }
`;

const ConfirmModalTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  padding-right: 32px;
`;

const ConfirmModalInfo = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  
  .plan-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .label {
      font-size: 13px;
      color: #6b7280;
      font-weight: 500;
    }
    
    .value {
      font-size: 15px;
      color: #1f2937;
      font-weight: 600;
      
      &.price {
        color: #667eea;
        font-size: 18px;
      }
    }
  }
`;

const ConfirmModalMessage = styled.p`
  margin: 0 0 16px 0;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  text-align: center;
`;

const ConfirmModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  max-width: 180px;

  &:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  max-width: 180px;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const PricingPage = () => {
  const [customAmount, setCustomAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [vipInfo, setVipInfo] = useState({ vipLevel: 'L0', isVip: false, subscription: null });
  const [vipInfoLoading, setVipInfoLoading] = useState(true); // 添加加载状态，初始为true
  const [showTooltip, setShowTooltip] = useState(false);
  const [subscriptionConfirm, setSubscriptionConfirm] = useState({ isOpen: false, plan: null });
  const [subscriptionPlans, setSubscriptionPlans] = useState(null);
  const { user } = useAuth();
  const userId = useMemo(() => {
    const id = user?.user_id;
    return id || null;
  }, [user]);

  // 获取订阅计划配置
  useEffect(() => {
    let isMounted = true;
    
    const loadSubscriptionPlans = async () => {
      try {
        const response = await PaymentAPI.getSubscriptionPlans();
        if (response.success && response.plans && isMounted) {
          setSubscriptionPlans(response.plans);
        }
      } catch (error) {
        if (isMounted) {
          setSubscriptionPlans({
            month: {
              id: 'month',
              title: '会员版 - 月卡',
              price: 69.9,
              credits: 120,
              duration: 30,
              period: '/ 月'
            },
            quarter: {
              id: 'quarter',
              title: '会员版 - 季卡',
              price: 179,
              credits: 360,
              duration: 90,
              period: '/ 季'
            }
          });
        }
      }
    };
    
    loadSubscriptionPlans();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // 获取VIP订阅信息
  useEffect(() => {
    let isMounted = true;
    
    const loadVipInfo = async () => {
      if (!userId) {
        setVipInfoLoading(false);
        return;
      }
      try {
        setVipInfoLoading(true);
        const response = await PaymentAPI.getUserSubscription(userId);
        if (response.success && isMounted) {
          setVipInfo({
            vipLevel: response.vip_level || 'L0',
            isVip: response.is_vip || false,
            subscription: response.subscription
          });
        }
      } catch (error) {
      } finally {
        if (isMounted) {
          setVipInfoLoading(false);
        }
      }
    };
    
    loadVipInfo();
    
    // 监听VIP信息更新事件
    const handleVipInfoUpdate = () => {
      loadVipInfo();
    };
    
    window.addEventListener('vipInfoUpdated', handleVipInfoUpdate);
    
    return () => {
      isMounted = false;
      window.removeEventListener('vipInfoUpdated', handleVipInfoUpdate);
    };
  }, [userId]);

  const showAlert = (title, message, type = 'info') => {
    setAlertModal({ isOpen: true, title, message, type });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, title: '', message: '', type: 'info' });
  };

  const handleTopUp = async () => {
    // 检查是否是体验版用户
    if (vipInfo.vipLevel === 'L0' && !vipInfo.isVip) {
      showAlert('提示', '积分不足时需充值补充（仅限会员用户）', 'warning');
      return;
    }

    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount <= 0) {
      showAlert('输入错误', '请输入有效的充值金额', 'error');
      return;
    }
    if (amount < 10) {
      showAlert('充值门槛', '充值金额需至少10元起', 'warning');
      return;
    }
    if (!userId) {
      showAlert('登录提示', '请先登录后再充值', 'warning');
      return;
    }
    
    setIsProcessing(true);
    try {
      // 创建充值支付订单（不是订阅订单）
      const orderData = {
        user_id: userId,
        subject: `职唛AI - 积分充值`,
        body: `充值${amount}元，获得${amount}积分`,
        amount: amount,
        subscription_plan: null,  // 充值订单，不是订阅
        subscription_duration: null,  // 充值订单，不需要订阅时长
        payment_method: 'alipay'  // 默认使用支付宝
      };
      
      const response = await PaymentAPI.createOrder(orderData);
      
      
      if (response.success && response.payment_url) {
        try {
          if (response.order_no) {
            localStorage.setItem('last_payment_order_no', response.order_no);
          }
        } catch {}
        // 跳转到支付页面
        document.open();
        document.write(response.payment_url);
        document.close();
      } else {
        showAlert('订单创建失败', '创建支付订单失败，请稍后重试', 'error');
      }
    } catch (error) {
      showAlert('充值失败', '创建充值订单失败，请稍后重试', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 判断是否是体验版用户
  const isTrialUser = vipInfo.vipLevel === 'L0' && !vipInfo.isVip;

  const membershipBenefits = [
    '解锁全部核心功能，畅享全部产品体验',
    '真人AI面试官，1积分/分钟，高度还原真实面试场景',
    '智能会议助手，1积分/分钟，实时识别关键问题并给出回答建议',
    '精准面试押题，5积分/次，基于目标岗位智能定制题目',
    '专业简历诊断，5积分/次，AI深度分析并提供优化建议',
    '简历模版下载，体验版5次/天，会员版20次/天，精选优质模板即用即下',
    '高精度语音识别与转写，全面洞察面试表现',
    '查看并下载所有诊断报告、面试记录和会议详情',
    '长期保存所有历史记录，支持随时查看和回顾',
    '多维度评估报告，提供详细改进建议和职业发展指导',
    '优先客服支持，享受更优质的服务体验'
  ];

  // 从API获取订阅计划配置，如果还未加载则使用默认值
  const cnPlans = subscriptionPlans ? [
    {
      id: 'month',
      title: subscriptionPlans.month.title,
      priceLabel: `¥ ${subscriptionPlans.month.price}`,
      priceValue: subscriptionPlans.month.price,
      period: subscriptionPlans.month.period,
      popular: true,
      credits: subscriptionPlans.month.credits
    },
    {
      id: 'quarter',
      title: subscriptionPlans.quarter.title,
      priceLabel: `¥ ${subscriptionPlans.quarter.price}`,
      priceValue: subscriptionPlans.quarter.price,
      period: subscriptionPlans.quarter.period,
      popular: false,
      credits: subscriptionPlans.quarter.credits
    }
  ] : [
    {
      id: 'month',
      title: '会员版 - 月卡',
      priceLabel: '¥ 69.9',
      priceValue: 69.9,
      period: '/ 月',
      popular: true,
      credits: 120
    },
    {
      id: 'quarter',
      title: '会员版 - 季卡',
      priceLabel: '¥ 179',
      priceValue: 179,
      period: '/ 季',
      popular: false,
      credits: 360
    }
  ];

  const handleSubscribe = (plan) => {
    if (!userId) {
      showAlert('登录提示', '请先登录后再订阅', 'warning');
      return;
    }

    // 显示订阅确认对话框
    setSubscriptionConfirm({ isOpen: true, plan });
  };

  const handleConfirmSubscribe = async () => {
    const { plan } = subscriptionConfirm;
    if (!plan) return;
    
    setSubscriptionConfirm({ isOpen: false, plan: null });
    setIsProcessing(true);
    
    try {
      // 计算订阅时长
      const durationMap = {
        'month': 30,
        'quarter': 90
      };
      
      const subscriptionDuration = durationMap[plan.id] || 30;
      
      // 创建支付订单
      const orderData = {
        user_id: userId,
        subject: `职唛AI Pro - ${plan.title}`,
        body: `订阅${plan.title}，享受${plan.credits}积分和全部会员权益`,
        amount: plan.priceValue,
        subscription_plan: plan.id,
        subscription_duration: subscriptionDuration,
        payment_method: 'alipay'
      };
      
      const response = await PaymentAPI.createOrder(orderData);
      
      
      if (response.success && response.payment_url) {
        try {
          if (response.order_no) {
            localStorage.setItem('last_payment_order_no', response.order_no);
          }
        } catch {}
        // 支付宝返回的是HTML表单，直接写入到新窗口或当前页面
        
        // 方法1：直接替换当前页面内容
        // 创建一个新的文档并写入支付表单
        document.open();
        document.write(response.payment_url);
        document.close();
      } else {
        showAlert('订单创建失败', '创建支付订单失败，请稍后重试', 'error');
      }
    } catch (error) {
      showAlert('订阅失败', '订阅失败，请稍后重试', 'error');
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <PageWrapper>
      <Title>{vipInfo.isVip ? '会员中心' : '立即升级'}</Title>
      
      <MainContent>
        <BenefitsSection>
          <h3>职唛AI Pro</h3>
          <p>{vipInfo.isVip ? '您已开通职唛AI Pro，全部核心功能与会员权益已解锁。右侧可定制充值积分，保障使用不中断。' : '成为会员，解锁全部核心功能，享受专业AI面试辅导，提升求职竞争力，获得更多面试机会。'}</p>
          
          <div style={{ borderTop: '1px solid #e5e7eb', width: '100px', margin: '24px 0 20px 0' }}></div>

          <h4>{vipInfo.isVip ? '会员权益概览' : '会员专属权益'}</h4>
          <BenefitsList>
            {membershipBenefits.map((feature, index) => (
              <li key={index}>
                <CheckCircle size={20} color="#10b981" />
                {feature}
              </li>
            ))}
          </BenefitsList>

          <SocialProof>
            <div style={{display: 'flex'}}>
              <img src="/images/upgrade/user_01.png" alt="user"/>
              <img src="/images/upgrade/user_02.png" alt="user"/>
              <img src="/images/upgrade/user_03.png" alt="user"/>
              <img src="/images/upgrade/user_04.png" alt="user"/>
              <img src="/images/upgrade/user_05.png" alt="user"/>
            </div>
            <div>
              <div className="stars">★★★★★</div>
              <div className="text">已有 10000+ 求职者信赖</div>
            </div>
          </SocialProof>
        </BenefitsSection>

        <PricingSection isVip={vipInfo.isVip}>
          {vipInfoLoading ? (
            <PlanCard>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '12px' }}>
                <Loader size={20} className="animate-spin" />
                <span style={{ color: '#6b7280' }}>加载会员信息中...</span>
              </div>
            </PlanCard>
          ) : (
            <>
              {!vipInfo.isVip && (
              <PlanCard>
                <h3>选择您的计划</h3>
                <p>订阅方式 – 由您选择</p>
                <PlansGrid>
                  {cnPlans.map((plan) => (
                    <Card 
                      key={plan.id} 
                      popular={plan.popular} 
                      onClick={() => !isProcessing && handleSubscribe(plan)}
                      style={{ 
                        opacity: isProcessing ? 0.7 : 1,
                        cursor: isProcessing ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {plan.popular && <PopularBadge>最受欢迎</PopularBadge>}
                      <PlanInfoWrapper>
                        <PlanInfo>{plan.title}</PlanInfo>
                        <PlanCredits>
                          {plan.id === 'month' && `每月 ${plan.credits} 积分`}
                          {plan.id === 'quarter' && `每季 ${plan.credits} 积分`}
                        </PlanCredits>
                      </PlanInfoWrapper>
                      <Price>
                        {isProcessing ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Loader size={16} className="animate-spin" />
                            处理中...
                          </div>
                        ) : (
                          <>
                            <span className="promo-tag">新品限时优惠</span>
                            <div className="current-row">
                              <span>{plan.priceLabel}</span>
                              <span className="period">{plan.period}</span>
                            </div>
                          </>
                        )}
                      </Price>
                    </Card>
                  ))}
                </PlansGrid>
              </PlanCard>
              )}
              
              {vipInfo.isVip && (
                <MemberBadge>
                  <div>您是尊贵的</div>
                  <div className="member-level">
                    {vipInfo.vipLevel === 'L1' ? '会员版 - 月卡会员' : vipInfo.vipLevel === 'L2' ? '会员版 - 季卡会员' : '会员版'}
                  </div>
                </MemberBadge>
              )}
            </>
          )}
          
          <CustomTopUpCard>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>定制充值</h3>
              {isTrialUser && (
                <span style={{ color: '#f59e0b', fontSize: '12px' }}>
                  会员用户适用
                </span>
              )}
            </div>
            <TopUpContainer>
              <Input
                type="number"
                min="1"
                placeholder="面试！冲冲冲！"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                disabled={isTrialUser}
                style={{ opacity: isTrialUser ? 0.6 : 1, cursor: isTrialUser ? 'not-allowed' : 'text' }}
              />
              <div style={{ position: 'relative' }}>
                <TopUpButton 
                  onClick={handleTopUp}
                  disabled={isTrialUser}
                  onMouseEnter={() => isTrialUser && setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  立即充值
                  {showTooltip && isTrialUser && (
                    <Tooltip show={showTooltip}>
                      积分不足时需充值补充（仅限会员用户）
                    </Tooltip>
                  )}
                </TopUpButton>
              </div>
            </TopUpContainer>
            <p style={{ marginTop: '12px', color: '#374151', fontSize: '13px', lineHeight: '1.4', fontWeight: '500' }}>
              充值后将获得对应积分，可用于按规则扣费
            </p>
          </CustomTopUpCard>
        </PricingSection>
      </MainContent>
      
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />

      {/* 订阅确认对话框 */}
      {subscriptionConfirm.isOpen && subscriptionConfirm.plan && (
        <SubscriptionConfirmModal onClick={(e) => e.target === e.currentTarget && setSubscriptionConfirm({ isOpen: false, plan: null })}>
          <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setSubscriptionConfirm({ isOpen: false, plan: null })}>
              <X size={20} />
            </CloseButton>
            <ConfirmModalTitle>订阅信息</ConfirmModalTitle>
            
            <ConfirmModalInfo>
              <div className="plan-item">
                <span className="label">订阅计划</span>
                <span className="value">{subscriptionConfirm.plan.title}</span>
              </div>
              <div className="plan-item">
                <span className="label">获得积分</span>
                <span className="value">{subscriptionConfirm.plan.credits} 积分</span>
              </div>
              <div className="plan-item">
                <span className="label">订阅价格</span>
                <span className="value price">{subscriptionConfirm.plan.priceLabel}{subscriptionConfirm.plan.period}</span>
              </div>
              <div className="plan-item">
                <span className="label">支付方式</span>
                <span className="value">支付宝</span>
              </div>
            </ConfirmModalInfo>
            
            <ConfirmModalMessage>
              确认后将跳转到支付页面完成付款，订阅成功后即可享受全部会员权益。
            </ConfirmModalMessage>
            
            <ConfirmModalActions>
              <CancelButton onClick={() => setSubscriptionConfirm({ isOpen: false, plan: null })}>
                取消
              </CancelButton>
              <ConfirmButton onClick={handleConfirmSubscribe}>
                确认
              </ConfirmButton>
            </ConfirmModalActions>
          </ConfirmModalContent>
        </SubscriptionConfirmModal>
      )}
    </PageWrapper>
  );
};

export default PricingPage;
